// src/features/rbac/components/AbilitiesTable.tsx
import React, { useState } from 'react';

import MinimalisticTable, {
  ColumnDef,
} from '../../../../shared/components/ui/MinimalisticTable';
import ConfirmDeleteButton
  from '../../../../shared/components/ui/ConfirmDeleteButton';
import { Ability, AbilityIn } from '../api/rbacApi';
import { useAbilities } from '../hooks/useAbilities';

/* ── Row-shape: real Ability plus optional helper flag ───────── */
type Row = Ability & { __isNew?: true };

export default function AbilitiesTable() {
  const {
    data: abilitiesRaw = [],
    isLoading,
    isError,
    error,
    add,
    remove,
  } = useAbilities();

  /* local state for inline “new” row */
  const [isAdding, setIsAdding] = useState(false);
  const [key,  setKey ] = useState('');
  const [desc, setDesc] = useState('');

  /* prepend the temporary row when adding */
  const tableData: Row[] = isAdding
    ? [
        {
          id_ability: 0,
          ability_key: '',
          description: '',
          __isNew: true,
        },
        ...abilitiesRaw,
      ]
    : abilitiesRaw;

  /* columns */
  const columns: ColumnDef<Row>[] = [
    { header: 'ID',          accessor: 'id_ability'   },
    { header: 'Ability Key', accessor: 'ability_key'  },
    {
      header: 'Description',
      accessor: 'description',
      Cell: (val: string) => (
        <span
          className="block max-w-[260px] truncate whitespace-nowrap"
          title={val}
        >
          {val}
        </span>
      ),
    },
    {
      header: <div className="flex justify-end pr-2" />,
      accessor: '',
      Cell: (_v, row) =>
        row.__isNew ? null : (
          <div className="flex justify-end pr-2">
            <ConfirmDeleteButton
              title="ability"
              onConfirm={() => remove.mutate(row.ability_key)}
            />
          </div>
        ),
    },
  ];

  /* create handler */
  const handleCreate = () => {
    const payload: AbilityIn = {
      ability_key: key.trim(),
      description: desc.trim() || undefined,
    };
    add.mutate(payload, {
      onSuccess: () => {
        setIsAdding(false);
        setKey('');
        setDesc('');
      },
    });
  };

  /* loading / error states */
  if (isLoading) return <p className="text-neutral-400">Loading…</p>;
  if (isError)   return <p className="text-rose-400">Error: {error?.message}</p>;

  /* render */
  return (
    <MinimalisticTable
      columns={columns}
      data={tableData}
      noDataMessage="No abilities."
      showSearch
      showNewButton={!isAdding}
      onNew={() => setIsAdding(true)}
      editableAccessors={['ability_key', 'description']}
      newRowValues={{ ability_key: key, description: desc }}
      onNewRowChange={(acc, val) => {
        if (acc === 'ability_key') setKey(String(val));
        if (acc === 'description') setDesc(String(val));
      }}
      onAcceptNew={handleCreate}
      onCancelNew={() => {
        setIsAdding(false);
        setKey('');
        setDesc('');
      }}
    />
  );
}
